import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import {
  FlaskConical,
  Plus,
  Search,
  Users,
  Calendar,
  ExternalLink,
  Image,
  ChevronRight,
  Filter,
  X,
  Lightbulb,
  Rocket,
  Code,
  CheckCircle,
  Edit,
  Trash2,
} from "lucide-react";

const WCALabs = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [editingProject, setEditingProject] = useState(null);
  const [newProject, setNewProject] = useState({
    name: "",
    description: "",
    status: "ideacion",
    division: "labs",
    team_members: [],
    kpis: {},
    gallery: [],
  });

  const isSuperAdmin = user?.is_super_admin || user?.role === "admin";

  const statuses = [
    { id: "ideacion", label: "Ideación", icon: Lightbulb, color: "bg-violet-100 text-violet-700" },
    { id: "prototipo", label: "Prototipo", icon: Code, color: "bg-amber-100 text-amber-700" },
    { id: "desarrollo", label: "Desarrollo", icon: Rocket, color: "bg-primary/10 text-primary" },
    { id: "lanzamiento", label: "Lanzamiento", icon: CheckCircle, color: "bg-emerald-100 text-emerald-700" },
    { id: "activo", label: "Activo", color: "bg-emerald-500 text-white" },
  ];

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects?division=labs`, {
        withCredentials: true,
      });
      setProjects(response.data || []);
    } catch (error) {
      console.error("Error fetching projects:", error);
      // Initialize with empty array for production
      setProjects([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!newProject.name.trim()) return;
    try {
      const response = await axios.post(`${API}/projects`, newProject, {
        withCredentials: true,
      });
      setProjects([...projects, response.data]);
    } catch (error) {
      console.error("Error creating project:", error);
      const mockProject = { project_id: `proj_${Date.now()}`, ...newProject };
      setProjects([...projects, mockProject]);
    }
    setShowModal(false);
    setEditingProject(null);
    setNewProject({
      name: "",
      description: "",
      status: "ideacion",
      division: "labs",
      team_members: [],
      kpis: {},
      gallery: [],
    });
  };

  const handleEditProject = (project) => {
    setEditingProject(project);
    setNewProject({
      name: project.name,
      description: project.description || "",
      status: project.status || "ideacion",
      division: project.division || "labs",
      team_members: project.team_members || [],
      kpis: project.kpis || {},
      gallery: project.gallery || [],
    });
    setShowModal(true);
  };

  const handleUpdateProject = async () => {
    if (!editingProject) return;
    try {
      await axios.put(`${API}/projects/${editingProject.project_id}`, newProject, {
        withCredentials: true,
      });
    } catch (error) {
      console.error("Error updating project:", error);
    }
    setProjects(projects.map(p => 
      p.project_id === editingProject.project_id
        ? { ...p, ...newProject }
        : p
    ));
    setShowModal(false);
    setEditingProject(null);
    setNewProject({
      name: "",
      description: "",
      status: "ideacion",
      division: "labs",
      team_members: [],
      kpis: {},
      gallery: [],
    });
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm("¿Estás seguro de eliminar este proyecto?")) return;
    try {
      await axios.delete(`${API}/projects/${projectId}`, { withCredentials: true });
    } catch (error) {
      console.error("Error deleting project:", error);
    }
    setProjects(projects.filter(p => p.project_id !== projectId));
    if (selectedProject && selectedProject.project_id === projectId) {
      setSelectedProject(null);
    }
  };

  const getStatusInfo = (statusId) => statuses.find((s) => s.id === statusId) || statuses[0];

  const projectsByStatus = (statusId) => projects.filter((p) => p.status === statusId);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="wca-labs">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center">
            <FlaskConical size={28} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">WCA | Labs</h1>
            <p className="text-slate-500">Portafolio de proyectos incubados</p>
          </div>
        </div>
        {isSuperAdmin && (
          <button
            onClick={() => {
              setEditingProject(null);
              setNewProject({
                name: "",
                description: "",
                status: "ideacion",
                division: "labs",
                team_members: [],
                kpis: {},
                gallery: [],
              });
              setShowModal(true);
            }}
            className="btn-primary flex items-center gap-2"
            data-testid="create-project-btn"
          >
            <Plus size={20} />
            Nuevo Proyecto
          </button>
        )}
      </div>

      {/* Status Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {statuses.map((status) => {
          const StatusIcon = status.icon || FlaskConical;
          return (
            <div key={status.id} className="glass-panel p-4 text-center">
              <div className={`w-10 h-10 rounded-xl ${status.color} mx-auto mb-2 flex items-center justify-center`}>
                <StatusIcon size={20} />
              </div>
              <p className="text-2xl font-bold text-slate-800">
                {projectsByStatus(status.id).length}
              </p>
              <p className="text-xs text-slate-500">{status.label}</p>
            </div>
          );
        })}
      </div>

      {/* Projects Grid */}
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="relative flex-1 max-w-sm">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              type="text"
              placeholder="Buscar proyectos..."
              className="w-full glass-input pl-10"
              data-testid="search-projects-input"
            />
          </div>
          <button className="btn-secondary flex items-center gap-2">
            <Filter size={18} />
            Filtrar
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project) => {
            const statusInfo = getStatusInfo(project.status);
            return (
              <div
                key={project.project_id}
                className="glass-panel overflow-hidden card-hover cursor-pointer"
                onClick={() => setSelectedProject(project)}
                data-testid={`project-${project.project_id}`}
              >
                {/* Gallery Preview */}
                <div className="aspect-video bg-slate-100 relative">
                  {project.gallery?.length > 0 ? (
                    <img
                      src={project.gallery[0]}
                      alt={project.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center">
                      <FlaskConical size={48} className="text-violet-300" />
                    </div>
                  )}
                  <div className="absolute top-3 right-3">
                    <span className={`badge ${statusInfo.color}`}>
                      {statusInfo.label}
                    </span>
                  </div>
                  {project.gallery?.length > 1 && (
                    <div className="absolute bottom-3 right-3 flex items-center gap-1 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
                      <Image size={12} />
                      {project.gallery.length}
                    </div>
                  )}
                </div>

                <div className="p-5">
                  <h3 className="font-semibold text-slate-800 mb-2">
                    {project.name}
                  </h3>
                  <p className="text-sm text-slate-500 line-clamp-2 mb-4">
                    {project.description}
                  </p>

                  <div className="flex items-center justify-between">
                    <div className="flex -space-x-2">
                      {project.team_members?.slice(0, 3).map((member, i) => (
                        <div
                          key={i}
                          className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-xs font-medium border-2 border-white"
                        >
                          {member.charAt(0)}
                        </div>
                      ))}
                      {project.team_members?.length > 3 && (
                        <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 text-xs font-medium border-2 border-white">
                          +{project.team_members.length - 3}
                        </div>
                      )}
                    </div>
                    <button className="btn-ghost text-sm flex items-center gap-1">
                      Ver <ChevronRight size={16} />
                    </button>
                  </div>
                  {isSuperAdmin && (
                    <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleEditProject(project); }}
                        className="flex-1 btn-secondary py-2 text-sm flex items-center justify-center gap-2"
                      >
                        <Edit size={14} />
                        Editar
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteProject(project.project_id); }}
                        className="p-2 hover:bg-rose-50 text-rose-600 rounded-lg transition-colors"
                        title="Eliminar"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Project Detail Modal */}
      {selectedProject && (
        <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-panel w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-slide-up" data-testid="project-detail-modal">
            {/* Gallery */}
            {selectedProject.gallery?.length > 0 && (
              <div className="aspect-video relative">
                <img
                  src={selectedProject.gallery[0]}
                  alt={selectedProject.name}
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => setSelectedProject(null)}
                  className="absolute top-4 right-4 p-2 bg-white/80 backdrop-blur-sm rounded-lg hover:bg-white transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            )}

            <div className="p-8">
              {!selectedProject.gallery?.length && (
                <div className="flex justify-end mb-4">
                  <button
                    onClick={() => setSelectedProject(null)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <X size={20} />
                  </button>
                </div>
              )}

              <div className="flex items-start justify-between mb-6">
                <div>
                  <span className={`badge ${getStatusInfo(selectedProject.status).color} mb-2`}>
                    {getStatusInfo(selectedProject.status).label}
                  </span>
                  <h2 className="text-2xl font-bold text-slate-800">
                    {selectedProject.name}
                  </h2>
                </div>
                {isSuperAdmin && (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setSelectedProject(null);
                        handleEditProject(selectedProject);
                      }}
                      className="p-2 hover:bg-primary/10 text-primary rounded-lg transition-colors"
                      title="Editar"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => {
                        handleDeleteProject(selectedProject.project_id);
                      }}
                      className="p-2 hover:bg-rose-50 text-rose-600 rounded-lg transition-colors"
                      title="Eliminar"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                )}
              </div>

              <p className="text-slate-600 mb-8">{selectedProject.description}</p>

              {/* KPIs */}
              {Object.keys(selectedProject.kpis || {}).length > 0 && (
                <div className="mb-8">
                  <h3 className="font-semibold text-slate-700 mb-4">KPIs del Proyecto</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(selectedProject.kpis).map(([key, value]) => (
                      <div key={key} className="p-4 rounded-2xl bg-slate-50 text-center">
                        <p className="text-2xl font-bold text-primary">{value}</p>
                        <p className="text-sm text-slate-500 capitalize">{key.replace("_", " ")}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Team */}
              {selectedProject.team_members?.length > 0 && (
                <div className="mb-8">
                  <h3 className="font-semibold text-slate-700 mb-4">Equipo</h3>
                  <div className="flex flex-wrap gap-3">
                    {selectedProject.team_members.map((member, i) => (
                      <div
                        key={i}
                        className="flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100"
                      >
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-sm font-medium">
                          {member.charAt(0)}
                        </div>
                        <span className="text-sm font-medium text-slate-700">{member}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Gallery */}
              {selectedProject.gallery?.length > 1 && (
                <div>
                  <h3 className="font-semibold text-slate-700 mb-4">Galería</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {selectedProject.gallery.map((img, i) => (
                      <img
                        key={i}
                        src={img}
                        alt={`${selectedProject.name} ${i + 1}`}
                        className="aspect-square object-cover rounded-2xl"
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Project Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="glass-panel p-8 w-full max-w-lg animate-slide-up" data-testid="create-project-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-slate-800">Nuevo Proyecto</h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Nombre del Proyecto
                </label>
                <input
                  type="text"
                  value={newProject.name}
                  onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                  className="w-full glass-input"
                  placeholder="Ej: EcoBot"
                  data-testid="project-name-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Descripción
                </label>
                <textarea
                  value={newProject.description}
                  onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                  className="w-full glass-input min-h-[100px]"
                  placeholder="Describe el proyecto y su propósito"
                  data-testid="project-description-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Estado
                </label>
                <select
                  value={newProject.status}
                  onChange={(e) => setNewProject({ ...newProject, status: e.target.value })}
                  className="w-full glass-input"
                  data-testid="project-status-select"
                >
                  {statuses.map((status) => (
                    <option key={status.id} value={status.id}>{status.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button 
                  onClick={() => {
                    setShowModal(false);
                    setEditingProject(null);
                  }} 
                  className="btn-secondary"
                >
                  Cancelar
                </button>
                <button 
                  onClick={editingProject ? handleUpdateProject : handleCreateProject} 
                  disabled={!newProject.name.trim()}
                  className="btn-primary" 
                  data-testid="save-project-btn"
                >
                  {editingProject ? "Guardar Cambios" : "Crear Proyecto"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WCALabs;
